import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const DOC_TYPE_NAMES = { "028": "Putovnica (HR)", "051": "Osobna iskaznica (HR)", "002": "Putovnica", "027": "Osobna iskaznica", "032": "Vozačka dozvola" };
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/datetime";
import api, { formatApiError } from "@/lib/api";
import { pickLocale } from "@/lib/locale";
import { Pager, PAGE_SIZE } from "@/components/Pager";
import {
  Plus, PencilSimple, Trash, X, CircleNotch, CheckCircle, Warning,
  ArrowsClockwise, IdentificationCard, ClockCounterClockwise, Buildings, MagnifyingGlass, Link as LinkIcon, Copy, PaperPlaneTilt, Lock, ArrowSquareOut,
} from "@phosphor-icons/react";

const STATUS_LABELS = {
  pending: { label: "Na čekanju", color: "bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  submitted: { label: "Prijavljeno", color: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  failed: { label: "Greška", color: "bg-destructive/20 text-destructive border-destructive/30" },
};

function L({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: "bg-muted text-foreground" };
  return <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold ${s.color}`} data-testid={`status-${status}`}>{s.label}</span>;
}

// =============================================================================
// Account form drawer
// =============================================================================
function AccountDrawer({ initial: initialProp, units, accounts = [], onClose, onSaved, onRefresh }) {
  const [initial, setInitial] = useState(initialProp || null);
  const isNew = !initial;
  const [form, setForm] = useState(() =>
    initial ? { ...initial, password: "" } : { account_name: "", username: "", password: "", unit_ids: [], facility_map: {} }
  );
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const facilities = (testResult?.facilities?.length ? testResult.facilities : null) || initial?.facilities_cache || [];

  // F8.9: build a map { unit_id → owner_account_name } for every unit assigned
  // to ANOTHER account. These checkboxes get disabled + tagged so user can't
  // double-assign the same unit to multiple eVisitor accounts.
  const unitOwnerMap = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      if (initial && a.id === initial.id) return;  // exclude self
      (a.unit_ids || []).forEach((uid) => {
        if (!map[uid]) map[uid] = a.account_name || a.username || "drugi račun";
      });
    });
    return map;
  }, [accounts, initial]);

  const toggleUnit = (unitId) => {
    setForm((f) => {
      const has = f.unit_ids.includes(unitId);
      return { ...f, unit_ids: has ? f.unit_ids.filter((id) => id !== unitId) : [...f.unit_ids, unitId] };
    });
  };

  const setFacilityFor = (unitId, code) => {
    setForm((f) => ({ ...f, facility_map: { ...f.facility_map, [unitId]: code } }));
  };

  const submit = async () => {
    setError("");
    if (!form.account_name?.trim() || !form.username?.trim()) {
      setError("Naziv i korisničko ime su obavezni");
      return;
    }
    if (isNew && !form.password?.trim()) {
      setError("Lozinka je obavezna za novi račun");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        account_name: form.account_name,
        username: form.username,
        unit_ids: form.unit_ids,
        facility_map: form.facility_map,
        legal_company_name: form.legal_company_name || null,
        legal_responsible_person: form.legal_responsible_person || null,
        legal_oib: form.legal_oib || null,
        legal_address: form.legal_address || null,
        legal_city: form.legal_city || null,
        legal_postal_code: form.legal_postal_code || null,
        legal_country: form.legal_country || "HR",
        legal_email: form.legal_email || null,
        legal_phone: form.legal_phone || null,
      };
      if (form.password) payload.password = form.password;
      if (isNew) {
        const resp = await api.post("/evisitor/accounts", payload);
        setInitial(resp.data);
        setForm((f) => ({ ...f, password: "" }));
        onRefresh?.();
        toast.success("Račun spremljen — dohvaćam objekte iz eVisitora...");
        await runTest(resp.data.id);
        return;
      }
      const resp = await api.put(`/evisitor/accounts/${initial.id}`, payload);
      onSaved(resp.data);
      onClose();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setError("");
    setTestResult(null);
    // Need to save first to test
    if (isNew) {
      setError("Spremi račun prije testiranja konekcije.");
      return;
    }
    if (form.password) {
      // save updated password first
      try {
        await api.put(`/evisitor/accounts/${initial.id}`, { password: form.password });
      } catch (e) {
        setError(formatApiError(e));
        return;
      }
    }
    await runTest(initial.id);
  };

  const runTest = async (accountId) => {
    setTesting(true);
    try {
      const { data } = await api.post(`/evisitor/accounts/${accountId}/test`);
      setTestResult(data);
      if (!data.ok) setError(data.error || "Test nije uspio");
      else {
        try {
          const { data: fresh } = await api.get("/evisitor/accounts");
          const me = (fresh || []).find((a) => a.id === accountId);
          if (me) setInitial(me);
        } catch { /* list refresh is best-effort */ }
        onRefresh?.();
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-xl flex-col border-l border-border bg-card shadow-2xl" data-testid="evisitor-account-drawer">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-foreground">{isNew ? "Novi eVisitor račun" : `Uredi: ${initial.account_name}`}</h3>
            <p className="text-[11px] text-muted-foreground">Vjerodajnice se spremaju enkriptirane (Fernet).</p>
          </div>
          <button onClick={onClose} className="rounded p-1.5 text-muted-foreground hover:bg-muted" data-testid="acc-close"><X size={18} /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="acc-error">{error}</div>}

          <L label="Naziv računa" hint="Interno ime — npr. 'Vlasnik Marko - Opatija'">
            <input className="rw-input" value={form.account_name} onChange={(e) => update("account_name", e.target.value)} data-testid="acc-name" />
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Korisničko ime">
              <input className="rw-input" value={form.username} onChange={(e) => update("username", e.target.value)} data-testid="acc-username" autoComplete="off" />
            </L>
            <L label={isNew ? "Lozinka" : "Nova lozinka (opcionalno)"}>
              <input type="password" className="rw-input" value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="acc-password" autoComplete="new-password" placeholder={isNew ? "" : "ostavi prazno da zadržiš postojeću"} />
            </L>
          </div>

          {!isNew && (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={test} disabled={testing} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50" data-testid="acc-test">
                {testing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} Testiraj i dohvati objekte
              </button>
              {initial?.last_login && <span className="text-[10px] text-muted-foreground">Zadnji login: {fmtDateTime(initial.last_login)}</span>}
              {initial?.last_error && <span className="text-[10px] text-destructive">{initial.last_error.slice(0, 80)}</span>}
            </div>
          )}

          {testResult?.ok && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300" data-testid="acc-test-ok">
              <CheckCircle size={14} weight="fill" className="inline mr-1" /> Konekcija uspješna! Pronađeno objekata: <b>{testResult.facility_count}</b>
            </div>
          )}

          {/* Unit mapping */}
          {/* F8.10: Voditelj obrade podataka (GDPR) — prikazuje se na public
              privacy stranici za sve jedinice dodijeljene ovom računu. */}
          <div className="space-y-2 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4" data-testid="acc-legal-section">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wide text-foreground">Voditelj obrade podataka · GDPR</div>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">obavezno za privacy policy</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Pravna osoba / fizička osoba koja je nositelj eVisitor računa i zakonski obrađuje osobne podatke gostiju. Pojavljuje se na javnoj stranici Politike privatnosti za sve jedinice dodijeljene ispod.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Naziv firme (ako postoji)</label>
                <input className="rw-input" placeholder="Lodgy obrt, j.d.o.o., ..." value={form.legal_company_name || ""} onChange={(e) => update("legal_company_name", e.target.value)} data-testid="acc-legal-company" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Odgovorna osoba *</label>
                <input className="rw-input" placeholder="Marko Horvat" value={form.legal_responsible_person || ""} onChange={(e) => update("legal_responsible_person", e.target.value)} data-testid="acc-legal-person" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">OIB *</label>
                <input className="rw-input font-mono" placeholder="12345678901" maxLength={11} value={form.legal_oib || ""} onChange={(e) => update("legal_oib", e.target.value.replace(/[^0-9]/g, ""))} data-testid="acc-legal-oib" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Email za privacy upite *</label>
                <input className="rw-input" type="email" placeholder="privacy@example.com" value={form.legal_email || ""} onChange={(e) => update("legal_email", e.target.value)} data-testid="acc-legal-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Ulica i broj</label>
                <input className="rw-input" placeholder="Ilica 100" value={form.legal_address || ""} onChange={(e) => update("legal_address", e.target.value)} data-testid="acc-legal-address" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Grad</label>
                <input className="rw-input" placeholder="Zagreb" value={form.legal_city || ""} onChange={(e) => update("legal_city", e.target.value)} data-testid="acc-legal-city" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Poštanski broj</label>
                <input className="rw-input" placeholder="10000" value={form.legal_postal_code || ""} onChange={(e) => update("legal_postal_code", e.target.value)} data-testid="acc-legal-postal" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Telefon</label>
                <input className="rw-input" placeholder="+385 91 234 5678" value={form.legal_phone || ""} onChange={(e) => update("legal_phone", e.target.value)} data-testid="acc-legal-phone" />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
            <div className="text-xs font-bold uppercase tracking-wide text-foreground">Dodijeli jedinice ovom računu</div>
            <p className="text-[11px] text-muted-foreground">Označi jedinice koje se prijavljuju kroz ovaj eVisitor račun, pa odaberi pripadajući objekt (facility_code).</p>
            {facilities.length === 0 && (
              <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-300">
                <Warning size={12} className="inline mr-1" /> Nema dohvaćenih objekata — klikni &quot;Testiraj&quot; da povučeš popis iz eVisitora.
              </div>
            )}
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {[...units]
                .sort((a, b) => {
                  // F8.9: units locked to another account go to the bottom
                  const aLocked = !!unitOwnerMap[a.id];
                  const bLocked = !!unitOwnerMap[b.id];
                  if (aLocked !== bLocked) return aLocked ? 1 : -1;
                  return (a.code || "").localeCompare(b.code || "");
                })
                .map((u) => {
                const checked = form.unit_ids.includes(u.id);
                const lockedBy = unitOwnerMap[u.id];  // owner name if assigned elsewhere
                const isLocked = !!lockedBy && !checked;
                return (
                  <div
                    key={u.id}
                    className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs transition ${
                      isLocked
                        ? "border-dashed border-border bg-muted/40 opacity-60"
                        : "border-border bg-card"
                    }`}
                    title={isLocked ? `Već dodijeljeno računu: ${lockedBy}` : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isLocked}
                      onChange={() => !isLocked && toggleUnit(u.id)}
                      className="h-4 w-4 accent-primary disabled:cursor-not-allowed"
                      data-testid={`acc-unit-cb-${u.code}`}
                    />
                    <div className="flex-1 truncate">
                      <span className={`font-semibold ${isLocked ? "text-muted-foreground line-through" : "text-foreground"}`}>{u.code}</span>{" "}
                      <span className="text-muted-foreground">— {pickLocale(u.name)}</span>
                      {isLocked && (
                        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                          <Lock size={8} weight="bold" /> {lockedBy}
                        </span>
                      )}
                    </div>
                    {checked && (
                      <select
                        className="rw-input !w-40 !py-1 !text-[11px]"
                        value={form.facility_map[u.id] || ""}
                        onChange={(e) => setFacilityFor(u.id, e.target.value)}
                        data-testid={`acc-unit-facility-${u.code}`}
                      >
                        <option value="">— odaberi objekt —</option>
                        {facilities.map((f) => (
                          <option key={f.code} value={f.code}>{f.name} ({f.code})</option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted">Odustani</button>
          <button onClick={submit} disabled={saving} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="acc-save">
            {saving ? "Spremam..." : isNew ? "Kreiraj" : "Spremi"}
          </button>
        </div>
      </aside>
      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); } .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary)/0.2); }`}</style>
    </>
  );
}

// =============================================================================
// Registration detail drawer
// =============================================================================
function RegistrationDrawer({ reg, accounts, units, properties, countries, onClose, onChanged }) {
  const [override, setOverride] = useState({
    evisitor_account_id: reg.evisitor_account_id || "",
    unit_id: reg.unit_id || "",
    facility_code: reg.facility_code_used || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const update = (k, v) => setOverride((o) => ({ ...o, [k]: v }));

  // When unit changes, auto-derive evisitor account + facility_code
  useEffect(() => {
    if (!override.unit_id) return;
    const acc = accounts.find((a) => a.unit_ids.includes(override.unit_id) && a.facility_map?.[override.unit_id]);
    if (acc) {
      setOverride((o) => ({ ...o, evisitor_account_id: acc.id, facility_code: acc.facility_map[override.unit_id] }));
    }
  }, [override.unit_id, accounts]);

  // Prefill the unit from the guest's reservation (linked or matched by surname + arrival).
  const [matchedRes, setMatchedRes] = useState(null);
  useEffect(() => {
    if (reg.status === "submitted") return;
    let cancelled = false;
    (async () => {
      try {
        let res = null;
        if (reg.reservation_id) {
          res = (await api.get(`/reservations/${reg.reservation_id}`)).data;
        } else if (reg.last_name && reg.arrival_date) {
          const a = new Date(`${reg.arrival_date}T00:00:00`);
          const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const { data } = await api.get("/reservations", { params: { q: reg.last_name, start: iso(new Date(a.getTime() - 30 * 86400000)), end: iso(new Date(a.getTime() + 2 * 86400000)) } });
          const list = Array.isArray(data) ? data : data?.items || [];
          res = list.find((r) => r.check_in === reg.arrival_date && r.status !== "cancelled")
            || list.find((r) => r.check_in <= reg.arrival_date && r.check_out > reg.arrival_date && r.status !== "cancelled") || null;
        }
        if (cancelled || !res) return;
        setMatchedRes(res);
        if (res.unit_id) setOverride((o) => (o.unit_id ? o : { ...o, unit_id: res.unit_id }));
      } catch { /* prefill is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [reg.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAccount = useMemo(() => accounts.find((a) => a.id === override.evisitor_account_id), [accounts, override.evisitor_account_id]);

  const submit = async () => {
    setError(""); setResult(null);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/evisitor/registrations/${reg.id}/submit`, {
        evisitor_account_id: override.evisitor_account_id || null,
        unit_id: override.unit_id || null,
        facility_code: override.facility_code || null,
      });
      setResult(data);
      if (data?.reservation?.id) window.dispatchEvent(new CustomEvent("rw-reservation-updated", { detail: data.reservation }));
      onChanged();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Izbriši ovu prijavu iz PMS-a?")) return;
    try {
      await api.delete(`/evisitor/registrations/${reg.id}`);
      onChanged();
      onClose();
    } catch (e) { setError(formatApiError(e)); }
  };

  const countryName = (code) => countries.find((c) => c.code === code)?.name || code;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-xl flex-col border-l border-border bg-card shadow-2xl" data-testid="reg-drawer">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-foreground">{reg.first_name} {reg.last_name}</h3>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <StatusBadge status={reg.status} />
              <span>· {reg.arrival_date} → {reg.departure_date}</span>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1.5 text-muted-foreground hover:bg-muted" data-testid="reg-close"><X size={18} /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
          {reg.error_message && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <b>eVisitor greška:</b> {reg.error_message}
            </div>
          )}
          {result?.ok && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
              <CheckCircle size={14} weight="fill" className="inline mr-1" /> Gost prijavljen u eVisitor! ({result.account})
            </div>
          )}

          <div className="rounded-md border border-border p-3">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">Podaci o gostu</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Info k="Spol" v={reg.gender === "M" ? "Muški" : "Ženski"} />
              <Info k="Datum rođenja" v={reg.date_of_birth} />
              <Info k="Državljanstvo" v={countryName(reg.citizenship)} />
              <Info k="Država prebivališta" v={countryName(reg.country_of_residence)} />
              <Info k="Grad prebivališta" v={reg.city_of_residence} />
              <Info k="Tip dokumenta" v={DOC_TYPE_NAMES[reg.document_type] ? `${DOC_TYPE_NAMES[reg.document_type]} (${reg.document_type})` : reg.document_type} />
              <Info k="Broj dokumenta" v={reg.document_number} />
              <Info k="Jezik forme" v={(reg.submission_lang || "hr").toUpperCase()} />
              {reg.is_lead_guest && <Info k="Vrsta" v="Nositelj rezervacije" />}
            </div>
          </div>

          {reg.status !== "submitted" && (
            <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-bold uppercase tracking-wide text-primary">Prijavi u eVisitor</div>
                {matchedRes && (
                  <span className="truncate rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300" title="Jedinica preuzeta iz rezervacije" data-testid="reg-prefill-badge">
                    Iz rezervacije {matchedRes.code || ""}
                  </span>
                )}
              </div>
              <L label="Jedinica (PMS)">
                <select className="rw-input" value={override.unit_id} onChange={(e) => update("unit_id", e.target.value)} data-testid="reg-unit-select">
                  <option value="">— odaberi jedinicu —</option>
                  {units.map((u) => {
                    const prop = properties.find((p) => p.id === u.property_id);
                    return <option key={u.id} value={u.id}>{u.code} — {pickLocale(prop?.name)}</option>;
                  })}
                </select>
              </L>
              <div className="grid grid-cols-2 gap-3">
                <L label="eVisitor račun">
                  <select className="rw-input" value={override.evisitor_account_id} onChange={(e) => update("evisitor_account_id", e.target.value)} data-testid="reg-account-select">
                    <option value="">— auto —</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                  </select>
                </L>
                <L label="Facility (objekt)">
                  <select className="rw-input" value={override.facility_code} onChange={(e) => update("facility_code", e.target.value)} data-testid="reg-facility-select">
                    <option value="">— auto —</option>
                    {(selectedAccount?.facilities_cache || []).map((f) => <option key={f.code} value={f.code}>{f.name} ({f.code})</option>)}
                  </select>
                </L>
              </div>
              <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="reg-submit">
                {submitting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />} Prijavi u eVisitor
              </button>
            </div>
          )}

          {reg.status === "submitted" && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
              <div className="font-semibold text-emerald-700 dark:text-emerald-300">Prijavljeno {reg.submitted_at && fmtDateTime(reg.submitted_at)}</div>
              <div className="mt-1 text-muted-foreground">Facility: <b>{reg.facility_code_used}</b></div>
            </div>
          )}

          <button onClick={remove} className="text-xs text-destructive hover:underline">Izbriši prijavu</button>
        </div>
      </aside>
      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); }`}</style>
    </>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
      <div className="text-foreground">{v || "—"}</div>
    </div>
  );
}

// =============================================================================
// Main page
// =============================================================================
export default function EVisitorPage() {
  const [tab, setTab] = useState("registrations"); // 'registrations' | 'accounts'
  const [accounts, setAccounts] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [editingAcc, setEditingAcc] = useState(null);
  const [creatingAcc, setCreatingAcc] = useState(false);
  const [openReg, setOpenReg] = useState(null);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep link from the calendar: /evisitor?reservation=<id> → open that guest's submitted check-in
  useEffect(() => {
    const rid = searchParams.get("reservation");
    if (!rid) return;
    setTab("registrations");
    api.get("/evisitor/registrations", { params: { reservation_id: rid } }).then(({ data }) => {
      const list = data || [];
      if (!list.length) { toast.info("Gost još nije ispunio check-in obrazac."); return; }
      setOpenReg(list.find((r) => r.is_lead_guest) || list[0]);
      if (list.length > 1) toast.info(`Prijava ima ${list.length} gostiju — ostali su na popisu ispod.`);
      const q = list[0].last_name || "";
      if (q) setFilter((f) => ({ ...f, q }));
    }).catch(() => {});
    const next = new URLSearchParams(searchParams); next.delete("reservation"); setSearchParams(next, { replace: true });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const loadedOnce = useRef(false);
  const refresh = async () => {
    if (!loadedOnce.current) setLoading(true);
    try {
      const [acc, regs, u, p, c, t] = await Promise.all([
        api.get("/evisitor/accounts"),
        api.get("/evisitor/registrations", { params: { status: filter.status || undefined, q: filter.q || undefined } }),
        api.get("/units"),
        api.get("/properties"),
        api.get("/evisitor/countries"),
        api.get("/tenants/me"),
      ]);
      setAccounts(acc.data); setRegistrations(regs.data); setUnits(u.data);
      setProperties(p.data); setCountries(c.data); setTenant(t.data);
    } finally { loadedOnce.current = true; setLoading(false); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loadedOnce.current) return;
    api.get("/evisitor/registrations", { params: { status: filter.status || undefined, q: filter.q || undefined } })
      .then(({ data }) => setRegistrations(data)).catch(() => {});
  }, [filter.status, filter.q]);

  const delAccount = async (id) => {
    if (!window.confirm("Izbrisati eVisitor račun?")) return;
    await api.delete(`/evisitor/accounts/${id}`);
    refresh();
  };

  const checkInUrl = tenant ? `${window.location.origin}/check-in/${tenant.slug}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(checkInUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_e) { /* noop */ }
  };

  return (
    <div className="space-y-5" data-testid="evisitor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="hidden sm:block">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">eVisitor prijava</h1>
          <p className="mt-1 text-sm text-muted-foreground">Prikupljanje podataka gostiju + zakonska prijava u HTZ-ov eVisitor sustav.</p>
        </div>
        {tenant && (
          <div className="flex min-w-0 max-w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs">
            <LinkIcon size={14} className="shrink-0 text-primary" />
            <span className="hidden shrink-0 text-muted-foreground sm:inline">Javni link za prijavu:</span>
            <code className="min-w-0 truncate font-mono text-[11px] text-foreground" data-testid="public-checkin-url">{checkInUrl}</code>
            <button onClick={copyLink} className="shrink-0 rounded px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10" data-testid="copy-link-btn">
              <Copy size={12} className="inline" /> {copied ? "Kopirano!" : "Kopiraj"}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border sm:gap-1">
        <TabBtn active={tab === "registrations"} onClick={() => setTab("registrations")} testId="tab-registrations">
          <ClockCounterClockwise size={14} weight="bold" /> <span className="hidden min-[400px]:inline">Prijave</span>
          <span className="ml-1 hidden rounded-full sm:inline bg-muted px-1.5 py-0.5 text-[10px] font-semibold">{registrations.length}</span>
        </TabBtn>
        <TabBtn active={tab === "accounts"} onClick={() => setTab("accounts")} testId="tab-accounts">
          <IdentificationCard size={14} weight="bold" /> <span className="hidden min-[400px]:inline sm:hidden">Računi</span><span className="hidden sm:inline">Postavke računa</span>
          <span className="ml-1 hidden rounded-full sm:inline bg-muted px-1.5 py-0.5 text-[10px] font-semibold">{accounts.length}</span>
        </TabBtn>
        <TabBtn active={tab === "links"} onClick={() => setTab("links")} testId="tab-links">
          <LinkIcon size={14} weight="bold" /> <span className="hidden min-[400px]:inline">Linkovi</span>
        </TabBtn>
        <TabBtn active={tab === "style"} onClick={() => setTab("style")} testId="tab-style">
          <Buildings size={14} weight="bold" /> <span className="hidden min-[400px]:inline">Stil</span>
        </TabBtn>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>
      ) : tab === "accounts" ? (
        <AccountsList accounts={accounts} units={units} properties={properties} onAdd={() => setCreatingAcc(true)} onEdit={setEditingAcc} onDel={delAccount} />
      ) : tab === "links" ? (
        <LinksTab tenant={tenant} properties={properties} accounts={accounts} units={units} />
      ) : tab === "style" ? (
        <StyleTab tenant={tenant} onSaved={refresh} />
      ) : (
        <RegistrationsList items={registrations} filter={filter} setFilter={setFilter} accounts={accounts} units={units} onOpen={setOpenReg} countries={countries} />
      )}

      {creatingAcc && (
        <AccountDrawer
          units={units}
          accounts={accounts}
          onClose={() => setCreatingAcc(false)}
          onSaved={() => { setCreatingAcc(false); refresh(); }}
          onRefresh={refresh}
        />
      )}
      {editingAcc && (
        <AccountDrawer
          initial={editingAcc}
          units={units}
          accounts={accounts}
          onClose={() => setEditingAcc(null)}
          onSaved={() => { setEditingAcc(null); refresh(); }}
          onRefresh={refresh}
        />
      )}
      {openReg && (
        <RegistrationDrawer
          reg={openReg}
          accounts={accounts}
          units={units}
          properties={properties}
          countries={countries}
          onClose={() => setOpenReg(null)}
          onChanged={() => { refresh(); setOpenReg(null); }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-2.5 text-sm font-medium transition-colors sm:flex-none sm:px-3 ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      data-testid={testId}
    >{children}</button>
  );
}

// =============================================================================
// Accounts list
// =============================================================================
function AccountsList({ accounts, units, properties, onAdd, onEdit, onDel }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={onAdd} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="acc-add">
          <Plus size={14} weight="bold" /> Dodaj eVisitor račun
        </button>
      </div>
      {accounts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nema podešenih eVisitor računa. Dodaj prvi račun i poveži ga s jedinicama.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {accounts.map((a) => (
            <div key={a.id} className="rounded-lg border border-border bg-card p-4" data-testid={`acc-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{a.account_name}</h3>
                  <p className="text-xs text-muted-foreground">{a.username}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => onEdit(a)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`acc-edit-${a.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => onDel(a.id)} className="rounded p-1.5 text-destructive hover:bg-destructive/10" data-testid={`acc-del-${a.id}`}><Trash size={14} /></button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                <Mini label="Jedinice" value={a.unit_ids.length} />
                <Mini label="Mapirano" value={Object.keys(a.facility_map || {}).length} />
                <Mini label="Objekata" value={(a.facilities_cache || []).length} />
              </div>
              {a.last_login && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                  <CheckCircle size={12} weight="fill" /> Konekcija OK — {fmtDateTime(a.last_login)}
                </div>
              )}
              {a.last_error && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-destructive">
                  <Warning size={12} weight="fill" className="mt-0.5 shrink-0" /> {a.last_error.slice(0, 100)}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-1">
                {a.unit_ids.map((uid) => {
                  const u = units.find((x) => x.id === uid);
                  if (!u) return null;
                  const facility = a.facility_map?.[uid];
                  return (
                    <span key={uid} className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${facility ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`} title={facility ? `Facility: ${facility}` : "Nije mapiran facility"}>
                      {u.code}{facility ? "" : " ⚠"}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded border border-border bg-muted/30 p-2">
      <div className="text-lg font-bold text-foreground">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

// =============================================================================
// Registrations list
// =============================================================================
function RegistrationsList({ items, filter, setFilter, accounts, units, onOpen, countries }) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [items]);
  const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const [q, setQ] = useState(filter.q);
  useEffect(() => { setQ(filter.q); }, [filter.q]);
  useEffect(() => {
    if (q === filter.q) return undefined;
    const t = setTimeout(() => setFilter((f) => ({ ...f, q })), 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Pretraži ime / prezime / broj dokumenta…"
            className="rw-input !pl-8 w-full sm:!w-72"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            enterKeyHint="search"
            data-testid="reg-search"
          />
        </div>
        <select
          className="rw-input !w-44"
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          data-testid="reg-status-filter"
        >
          <option value="">Svi statusi</option>
          <option value="pending">Na čekanju</option>
          <option value="submitted">Prijavljeno</option>
          <option value="failed">Greška</option>
        </select>
      </div>
      {items.length === 0 && filter.q ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="reg-no-results">
          Nema rezultata za „{filter.q}".
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nema prijava. Pošalji javni link gostima da ispune formu, ili pričekaj da gost ispuni vlastoručno.
        </div>
      ) : (
        <>
        <Pager page={page} total={items.length} onChange={setPage} testId="reg-pager" />
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card md:hidden" data-testid="registrations-cards">
          {pageItems.map((r) => (
            <button key={r.id} type="button" onClick={() => onOpen(r)} className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-muted/30" data-testid={`reg-card-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{r.first_name} {r.last_name}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">
                  {r.arrival_date} → {r.departure_date} · {r.document_number || r.document_type}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="hidden overflow-x-auto rounded-lg border border-border bg-card md:block">
          <table className="w-full whitespace-nowrap text-sm" data-testid="registrations-table">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Gost</th>
                <th className="px-3 py-2 text-left">Dolazak</th>
                <th className="px-3 py-2 text-left">Odlazak</th>
                <th className="px-3 py-2 text-left">Državljanstvo</th>
                <th className="px-3 py-2 text-left">Dokument</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Predano</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((r) => (
                <tr key={r.id} onClick={() => onOpen(r)} className="cursor-pointer border-t border-border hover:bg-muted/30" data-testid={`reg-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-foreground">{r.first_name} {r.last_name}</div>
                    <div className="text-[11px] text-muted-foreground">{r.is_lead_guest ? "Nositelj rezervacije" : "Sudionik"} · {(r.submission_lang || "hr").toUpperCase()}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.arrival_date} <span className="text-muted-foreground">{r.arrival_time}</span></td>
                  <td className="px-3 py-2 text-xs">{r.departure_date} <span className="text-muted-foreground">{r.departure_time}</span></td>
                  <td className="px-3 py-2 text-xs">{countries.find((c) => c.code === r.citizenship)?.name || r.citizenship}</td>
                  <td className="px-3 py-2 text-xs">{DOC_TYPE_NAMES[r.document_type] || r.document_type} · {r.document_number}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">{r.submitted_at ? fmtDateTime(r.submitted_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); }`}</style>
    </div>
  );
}

// =============================================================================
// Links tab — per-property check-in URLs
// =============================================================================
function controllerForProperty(p, accounts, units) {
  const unitIds = new Set(units.filter((u) => u.property_id === p.id).map((u) => u.id));
  const acc = accounts.find((a) => a.active !== false
    && (a.legal_company_name || a.legal_responsible_person || a.legal_oib)
    && (a.unit_ids || []).some((id) => unitIds.has(id)));
  if (acc) return acc.legal_company_name || acc.legal_responsible_person || `OIB ${acc.legal_oib}`;
  return p.legal_company_name || p.legal_responsible_person || "";
}

function LinksTab({ tenant, properties, accounts = [], units = [] }) {
  const [copied, setCopied] = useState("");
  if (!tenant) return null;
  const base = `${window.location.origin}/check-in/${tenant.slug}`;
  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopied(url); setTimeout(() => setCopied(""), 2000); } catch (_e) { /* noop */ }
  };
  return (
    <div className="space-y-4" data-testid="links-tab">
      <p className="text-sm text-muted-foreground">Svaki objekt ima svoj check-in link za goste.</p>
      <div className="space-y-2">
        {properties.map((p) => {
          const url = `${base}/p/${p.id}`;
          return <LinkRow key={p.id} label={pickLocale(p.name)} sub={controllerForProperty(p, accounts, units) || "⚠ Nedostaje voditelj obrade"} url={url} copied={copied === url} onCopy={() => copy(url)} testId={`link-prop-${p.id}`} />;
        })}
      </div>
    </div>
  );
}

function LinkRow({ label, sub, url, copied, onCopy, testId }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">{label}</div>
        {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
        <code className="mt-1 block truncate font-mono text-[11px] text-foreground/70" title={url}>{url.replace(/^https?:\/\//, "")}</code>
      </div>
      <button onClick={onCopy} title="Kopiraj link" aria-label="Kopiraj link" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-foreground hover:bg-muted">
        {copied ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : <Copy size={16} />}
      </button>
      <a href={url} target="_blank" rel="noreferrer" title="Otvori" aria-label="Otvori" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90">
        <ArrowSquareOut size={16} />
      </a>
    </div>
  );
}

// =============================================================================
// Style tab — set check-in form style (independent of PMS theme)
// =============================================================================
const FORM_FONT_OPTIONS = [
  "Playfair Display", "Inter", "Roboto", "Lato", "Open Sans", "Poppins", "Montserrat", "Nunito",
  "Raleway", "Merriweather", "Lora", "Cormorant Garamond", "Marcellus", "DM Serif Display",
  "EB Garamond", "Libre Baskerville", "Bitter", "Source Sans Pro", "Crimson Text", "PT Serif",
  "Manrope", "Karla", "Work Sans", "Quicksand",
];

function StyleTab({ tenant, onSaved }) {
  const defaults = tenant?.checkin_form_style || {};
  // Lodgy.co brand palette provided by the user:
  //   #001A38 primary navy · #19304B secondary navy · #06438A accent blue
  //   #11A285 teal (success/accent) · #FF990A amber (warning/highlight)
  //   #FFFEFF near-white · #000205 near-black
  const LODGY_DEFAULTS = {
    primary_color: "#0E1F3A",
    primary_hover: "#234388",
    background_color: "#EBEBEB",
    card_background: "#FFFFFF",
    text_color: "#01070D",
    text_muted: "#23334C",
    border_color: "#E2E8F0",
    warning_bg: "#FFF3E0",
    warning_text: "#FF990A",
    heading_font: "Inter",
    body_font: "Inter",
    radius: "10",
  };
  const [style, setStyle] = useState({
    primary_color: defaults.primary_color || LODGY_DEFAULTS.primary_color,
    primary_hover: defaults.primary_hover || LODGY_DEFAULTS.primary_hover,
    background_color: defaults.background_color || LODGY_DEFAULTS.background_color,
    card_background: defaults.card_background || LODGY_DEFAULTS.card_background,
    text_color: defaults.text_color || LODGY_DEFAULTS.text_color,
    text_muted: defaults.text_muted || LODGY_DEFAULTS.text_muted,
    border_color: defaults.border_color || LODGY_DEFAULTS.border_color,
    warning_bg: defaults.warning_bg || LODGY_DEFAULTS.warning_bg,
    warning_text: defaults.warning_text || LODGY_DEFAULTS.warning_text,
    heading_font: defaults.heading_font || LODGY_DEFAULTS.heading_font,
    body_font: defaults.body_font || LODGY_DEFAULTS.body_font,
    radius: defaults.radius || LODGY_DEFAULTS.radius,
    logo_url: defaults.logo_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const upd = (k, v) => setStyle((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/tenants/me", { checkin_form_style: style });
      setMsg("Spremljeno!");
      onSaved && onSaved();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = () => setStyle({ ...LODGY_DEFAULTS, logo_url: style.logo_url });

  return (
    <div className="space-y-5" data-testid="style-tab">
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-1 text-base font-semibold text-foreground">Stil javne check-in forme</h3>
        <p className="text-xs text-muted-foreground">Ove postavke se odnose <b>samo</b> na javnu formu za prijavu gostiju. Tema admin sučelja se postavlja zasebno u tab-u &quot;Tema&quot;.</p>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Colors */}
          <div className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-wide text-foreground">Boje</div>
            <ColorRow label="Primarna" k="primary_color" style={style} upd={upd} />
            <ColorRow label="Primarna (hover)" k="primary_hover" style={style} upd={upd} />
            <ColorRow label="Pozadina stranice" k="background_color" style={style} upd={upd} />
            <ColorRow label="Pozadina kartice" k="card_background" style={style} upd={upd} />
            <ColorRow label="Tekst" k="text_color" style={style} upd={upd} />
            <ColorRow label="Sekundarni tekst" k="text_muted" style={style} upd={upd} />
            <ColorRow label="Rubovi" k="border_color" style={style} upd={upd} />
            <ColorRow label="Upozorenje (pozadina)" k="warning_bg" style={style} upd={upd} />
            <ColorRow label="Upozorenje (tekst)" k="warning_text" style={style} upd={upd} />
          </div>

          {/* Typography + radius */}
          <div className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-wide text-foreground">Tipografija i logo</div>
            <FontSelect label="Naslovi" k="heading_font" style={style} upd={upd} />
            <FontSelect label="Tekst" k="body_font" style={style} upd={upd} />
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">URL logotipa (forma)</label>
              <input
                type="url"
                placeholder="https://example.com/logo.png — prazno = koristi admin logo"
                className="rw-input"
                value={style.logo_url || ""}
                onChange={(e) => upd("logo_url", e.target.value)}
                data-testid="style-logo-url"
              />
              <p className="mt-0.5 text-[10px] text-muted-foreground">Ako je prazno, koristi se logo iz Theme & Branding admin sučelja.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">Zaobljenje rubova ({style.radius}px)</label>
              <input type="range" min="0" max="32" value={style.radius} onChange={(e) => upd("radius", e.target.value)} className="w-full" data-testid="style-radius" />
            </div>

            <div className="mt-5 rounded-lg border-2 p-5" style={{ borderColor: style.border_color, background: style.background_color, borderRadius: `${style.radius}px` }}>
              <div className="rounded-lg p-4" style={{ background: style.card_background, borderRadius: `${Math.max(0, style.radius - 2)}px` }}>
                <div className="text-center text-2xl font-bold" style={{ color: style.text_color, fontFamily: `'${style.heading_font}', serif` }} data-testid="style-preview-title">
                  {tenant?.brand_name || tenant?.name || "Vaš smještaj"}
                </div>
                <div className="mx-auto mt-2 h-0.5 w-12" style={{ background: style.primary_color }} />
                <p className="mt-3 text-center text-xs" style={{ color: style.text_muted, fontFamily: `'${style.body_font}', sans-serif` }}>
                  Pregled stila — naslovi koriste {style.heading_font}, tekst {style.body_font}.
                </p>
                <button className="mt-4 w-full rounded-md py-2 text-sm font-bold text-white" style={{ background: style.primary_color, borderRadius: `${Math.max(0, style.radius - 2)}px` }}>
                  Pošalji prijavu
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <button onClick={save} disabled={saving} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="style-save">
            {saving ? "Spremam..." : "Spremi stil"}
          </button>
          <button onClick={reset} className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted" data-testid="style-reset">
            Vrati na zadane vrijednosti
          </button>
          {msg && <span className="text-xs text-emerald-600 dark:text-emerald-400">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function ColorRow({ label, k, style, upd }) {
  return (
    <div className="flex items-center gap-3">
      <label className="flex-1 text-xs text-foreground">{label}</label>
      <input
        type="color"
        value={style[k] || "#000000"}
        onChange={(e) => upd(k, e.target.value)}
        className="h-8 w-12 cursor-pointer rounded border border-border"
        data-testid={`style-${k}`}
      />
      <input
        type="text"
        value={style[k] || ""}
        onChange={(e) => upd(k, e.target.value)}
        className="w-24 rounded border border-border bg-card px-2 py-1 font-mono text-[11px] uppercase text-foreground"
      />
    </div>
  );
}

function FontSelect({ label, k, style, upd }) {
  // load font in real-time for preview
  useEffect(() => {
    const f = style[k];
    if (!f) return;
    const id = `gf-${k}-${f.replace(/\s/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id; link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [style, k]);
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-foreground">{label}</label>
      <select className="rw-input" value={style[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`style-${k}`}>
        {FORM_FONT_OPTIONS.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}', serif` }}>{f}</option>)}
      </select>
    </div>
  );
}

